import type { Ref } from 'vue';
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { createGlobalState } from '@vueuse/core';
import type { HistoryRowData, NavMenuItem } from '../types';
import { getHistoryList } from '../infrastructure/api-functions';
import { useCachedOpened } from './use-cached-opended';
import { create } from '@eureka/utils/deferred';
import { getHistoryRouterData } from '../infrastructure/utils/history-router';
import _ from 'lodash';
import { formatDateToLocal } from '@eureka/utils/date-format';
import { getCurrentLanguage } from '@eureka/language-tool';

export const useNavBarState = createGlobalState(
    () => {
        const { load, save } = useCachedOpened();

        const currentLang = computed(() => getCurrentLanguage() as 'cn' | 'en' | 'jp' | 'tw');

        const opened = ref(load());

        const shouldSave = ref(true);

        const loading = ref(true);

        const route = useRoute();

        const listDeferred = create<NavMenuItem[]>();

        const historyList: Ref<NavMenuItem[]> = ref([]);

        const currentDetailId = computed(() => {
            return route.params?.seek_id || route.params?.board_id || route.params?.projectId || route.params?.fmea_id || route.query?.board_id || route.params?.taskId || route.params?.id || route.params?.agent_job_id || route.params?.lsAgentId || route.params?.graph_id || route.params?.triz_mind_id || route.params?.conversation_id
        })

        const currentRouteFlag = computed(() => {
            return route.path + currentDetailId.value;
        })

        const routeName = computed(() => {
            return route.name;
        })

        const sessionsTaskData = ref<any[]>([]);

        function updateNavStatus(value: boolean, save = true) {
            opened.value = value;
            shouldSave.value = save;
            // 确保在下一个 DOM 更新周期中重新启用保存
            nextTick(() => {
                shouldSave.value = true;
            });
        }

        function updateHistory(list: NavMenuItem[]) {
            historyList.value = list;
        }

        function queryList() {
            loading.value = true;
            getHistoryList().then((res) => {
                const data = _.flatMap(res.history_maps, 'data') as HistoryRowData[]
                historyList.value = data.map(item => {
                    const { router, module } = getHistoryRouterData(item.source, item.id);
                    // 兼容ls
                    const link = (item?.rawType.startsWith('ls') && !item.link.startsWith('/ls/#')) ? `/ls/#${item.link}` : item.link
                    return {
                        type: item.source,
                        id: item.id,
                        title: item.message,
                        link: link,
                        router,
                        module,
                        icon_url: item.icon_url,
                        business_name: item.business_name,
                        active: item.id === currentDetailId.value,
                    }
                })
                listDeferred.resolve(historyList.value)
                loading.value = false;
            });
        }

        watch(currentDetailId, () => {
            historyList.value = historyList.value.map(item => ({ ...item, active: item.id === currentDetailId.value }))
        })

        async function getSessionsTaskData(query: string = '') {
            const res = await getHistoryList(query);
            sessionsTaskData.value = _.flatMap(res.history_maps, 'data').map((item: HistoryRowData) => {
                return {
                    ...item,
                    created_at: formatDateToLocal({ date: item.created_at, currentLang: currentLang.value }),
                }
            });
        }

        return {
            opened,
            loading,
            historyList,
            currentDetailId,
            routeName,
            currentRouteFlag,
            updateNavStatus,
            queryList,
            updateHistory,
            save,
            shouldSave,
            getSessionsTaskData,
            sessionsTaskData,
        }
    }
)
